// TIP-614 — buy extra briefs. The checkout flow, in two steps.
//
// Design: `Checkout Flow.dc.html`, sections 2a-2d and 3c.
//
//   step 1  "ours". What you are buying, what it costs, one action. A quantity
//           stepper and a summary. NO CARD FIELDS — the design is explicit
//           that card entry belongs to Stripe on the next screen.
//   bridge  a card-shaped skeleton while the session is created.
//   step 2  the summary pinned left, Stripe Embedded Checkout on the right,
//           and no pay button of ours anywhere near it.
//   error   session creation failed. Nothing charged, quantity kept, retry.
//   done    payment received, briefs credited.
//
// The quote drawn here is a DISPLAY ARTIFACT. Quantity, tier and price are
// never sent: the backend validates 1-20 itself, reads the tier from
// Company.is_member and the price from settings, and Stripe's session totals
// are the record. So this modal can be wrong about the price without being
// able to charge the wrong price.
import React, { useCallback, useEffect, useMemo, useState } from "react";
import "./counterpartyBriefs.css";
import StripeCheckoutPanel from "../../../common/StripeCheckoutPanel";
import { BUY_COPY } from "./buyBriefsCopy";
import {
  canPrice,
  clampQuantity,
  formatAed,
  listNetFils,
  quantityBounds,
  quote,
  savingsFils,
} from "../../../../constants/counterpartyBriefPricing";

/**
 * The money on step 1: the actual amount, excluding tax.
 *
 * NO TAX LINE, for anybody. Stripe computes tax from the billing address
 * collected inside its block, which nothing here can know — and which is a
 * different thing from the company's registered country, so deriving it from
 * that was wrong even when it happened to agree. The total is marked
 * "(excluding tax)" and Stripe adds whatever applies on the next screen.
 */
const OrderSummary = ({ quantity, isMember, pricing }) => {
  const priced = quote(quantity, isMember, pricing);
  const list = listNetFils(quantity, pricing);
  if (!priced) return null;

  return (
    <div className="cb-buy-summary">
      <div className="cb-buy-row">
        <span className="cb-buy-row-label">{BUY_COPY.lineLabel(quantity)}</span>
        <span className="cb-buy-row-values">
          {isMember && list !== null && list > priced.netFils && (
            <span className="cb-buy-was" data-testid="cb-buy-was">
              {formatAed(list)}
            </span>
          )}
          <span className="cb-buy-row-value" data-testid="cb-buy-net">
            {formatAed(priced.netFils)}
          </span>
        </span>
      </div>

      <div className="cb-buy-total">
        <span className="cb-buy-total-label">
          {BUY_COPY.totalLabel}{" "}
          <span className="cb-buy-extax">{BUY_COPY.excludingTax}</span>
        </span>
        <span className="cb-buy-total-value" data-testid="cb-buy-total">
          {formatAed(priced.netFils)}
        </span>
      </div>
    </div>
  );
};

const BuyBriefsModal = ({
  allowance,
  // The `pricing` block from GET /api/briefs/allowance/, via
  // store/briefs/briefPricingSlice. THE source of every figure drawn here.
  pricing: pricingProp = null,
  onClose,
  // Creates the Stripe session. Injected, so this modal touches no store and
  // can be rendered in a test without a Provider — the same shape the order
  // form uses for its reference lookup.
  onCreateSession,
  // "See membership" on the standard-price upsell. Goes to the membership
  // page today; TIP-615 replaces it with the membership step in this modal.
  onBecomeMember,
  // After a credited purchase: order one now, or read the receipt.
  onOrder,
  // The credited purchase, once the poll says so: { quantity, chargedTotalFils,
  // reference, bought, receiptUrl }. Null until then.
  purchase = null,
  // Still asking the backend whether the credit has landed.
  purchaseCrediting = false,
  // Asked for a minute and it has not. Same reassurance, different copy.
  purchasePending = false,
  // Called with the Checkout session id once Stripe reports the payment
  // complete without redirecting. Starts the credit poll in place.
  onPaid,
}) => {
  const isMember = !!allowance?.isMember;
  // The endpoint's pricing block, verbatim. The maths prefers it over the
  // frontend's fallback constants.
  // Handed in from the store. `allowance.pricing` is honoured too so a caller
  // that already had the block does not have to thread a second prop.
  const pricing = pricingProp || allowance?.pricing || null;
  const priceable = canPrice(pricing, isMember);
  // The purchasable range is the endpoint's too, not a constant: one
  // environment already serves max_quantity 100 against a settings default of
  // 20, and a hardcoded cap would refuse briefs the backend would sell.
  const bounds = useMemo(() => quantityBounds(pricing), [pricing]);
  const memberUnit = allowance?.memberUnit || null;
  const unit = allowance?.unit || null;

  const [quantity, setQuantity] = useState(1);
  const [clientSecret, setClientSecret] = useState(null);
  // The session behind `clientSecret`. Kept because the stay-on-page flow has
  // no redirect and therefore no ?purchase=<id> to read it back from — this is
  // what the credit poll is keyed on.
  const [sessionId, setSessionId] = useState(null);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState(null);

  // "done" and "pending" are driven from outside: the customer left for
  // Stripe and came back to a fresh page, so this modal is re-opened by the
  // return URL rather than kept alive across the redirect.
  const step = useMemo(() => {
    if (purchase) return "done";
    if (purchasePending) return "pending";
    if (purchaseCrediting) return "crediting";
    if (error) return "error";
    if (clientSecret || creating) return "payment";
    return "quantity";
  }, [purchase, purchasePending, purchaseCrediting, error, clientSecret, creating]);

  const onQuantity = useCallback(
    (next) => setQuantity(clampQuantity(next, pricing)),
    [pricing]
  );

  // The allowance can land after the modal is open, and its bounds may not
  // contain the quantity showing. Pulled into range rather than left to be
  // rejected on submit.
  useEffect(() => {
    setQuantity((q) => clampQuantity(q, pricing));
  }, [pricing]);

  const start = useCallback(async () => {
    setError(null);
    setCreating(true);
    const result = await onCreateSession({ quantity });
    setCreating(false);
    if (result?.clientSecret) {
      setClientSecret(result.clientSecret);
      setSessionId(result.sessionId || null);
      return;
    }
    // Nothing was charged: the session was never created. The quantity is
    // kept so Try again is one press, not a re-entry.
    setError(result?.error || { message: BUY_COPY.errorBody });
  }, [onCreateSession, quantity]);

  // Back from step 2 discards the session. Reusing it after the customer
  // changed their quantity would charge for the old one — the line items are
  // fixed when the session is created.
  const back = useCallback(() => {
    setClientSecret(null);
    setSessionId(null);
    setError(null);
  }, []);

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);


  return (
    /* --sheet is what turns this into a full-screen sheet on a phone. A class
       rather than `:has(.cb-buy-modal)` in the stylesheet, because `:has` is
       still missing from older Safari and the mobile layout is exactly where
       that matters most. */
    <div className="cb-modal-backdrop cb-modal-backdrop--sheet" onClick={onClose}>
      <div
        role="dialog"
        aria-modal="true"
        aria-label={BUY_COPY.title}
        data-testid="cb-buy-modal"
        data-step={step}
        className="cb-modal cb-buy-modal"
        onClick={(e) => e.stopPropagation()}
      >
        {step === "quantity" && (
          <>
            <div className="cb-modal-head">
              <div>
                <p className="cb-modal-eyebrow">{BUY_COPY.eyebrow}</p>
                <h3 className="cb-modal-title">{BUY_COPY.title}</h3>
                <p className="cb-buy-sub">{BUY_COPY.sub}</p>
              </div>
              <button
                type="button"
                className="cb-modal-close"
                onClick={onClose}
                aria-label={BUY_COPY.close}
              >
                &#215;
              </button>
            </div>

            <div className="cb-buy-body">
              <div className="cb-buy-qty">
                <div>
                  <p className="cb-buy-qty-label">{BUY_COPY.quantityLabel}</p>
                  {unit && (
                    <p className="cb-buy-qty-unit" data-testid="cb-buy-unit">
                      {BUY_COPY.unitNote(unit, isMember)}
                    </p>
                  )}
                </div>
                <div className="cb-buy-stepper">
                  <button
                    type="button"
                    className="cb-buy-stepper-btn"
                    onClick={() => onQuantity(quantity - 1)}
                    disabled={quantity <= bounds.min}
                    aria-label={BUY_COPY.decrease}
                  >
                    &#8722;
                  </button>
                  <span
                    className="cb-buy-stepper-value"
                    data-testid="cb-buy-quantity"
                    aria-live="polite"
                  >
                    {BUY_COPY.quantityValue(quantity)}
                  </span>
                  <button
                    type="button"
                    className="cb-buy-stepper-btn"
                    onClick={() => onQuantity(quantity + 1)}
                    disabled={quantity >= bounds.max}
                    aria-label={BUY_COPY.increase}
                  >
                    +
                  </button>
                </div>
              </div>

              {/* Only a member has a saving, and the figure comes from the two
                  unit prices rather than a percentage. */}
              {isMember && savingsFils(quantity, true, pricing) > 0 && (
                <div className="cb-buy-saving" data-testid="cb-buy-saving">
                  <p>
                    {BUY_COPY.savings(
                      formatAed(savingsFils(quantity, true, pricing)),
                      quantity
                    )}
                  </p>
                </div>
              )}

              {priceable ? (
                <OrderSummary quantity={quantity} isMember={isMember} pricing={pricing} />
              ) : (
                /* The endpoint has not told us the price. Saying so is the
                   only honest option: a figure invented here would differ
                   from Stripe's on the next screen. */
                <p className="cb-buy-noprice" role="status" data-testid="cb-buy-noprice">
                  {BUY_COPY.noPrice}
                </p>
              )}

              <p className="cb-buy-note">{BUY_COPY.noExpiry}</p>

              {/* The standard-price upsell. Absent for a member, who has
                  nothing to be sold. */}
              {!isMember && memberUnit && (
                <div className="cb-buy-upsell" data-testid="cb-buy-upsell">
                  <p className="cb-buy-upsell-text">{BUY_COPY.upsell(memberUnit)}</p>
                  <button type="button" className="cb-buy-upsell-btn" onClick={onBecomeMember}>
                    {BUY_COPY.seeMembership}
                  </button>
                </div>
              )}
            </div>

            <div className="cb-buy-foot">
              <button
                type="button"
                className="cb-buy-cta"
                onClick={start}
                disabled={!priceable}
                data-testid="cb-buy-continue"
              >
                {BUY_COPY.cta}
              </button>
              <div className="cb-buy-foot-row">
                <span className="cb-buy-helper">{BUY_COPY.ctaHelper}</span>
                <button type="button" className="cb-buy-quiet" onClick={onClose}>
                  {BUY_COPY.notNow}
                </button>
              </div>
            </div>
          </>
        )}

        {step === "payment" && (
          /* STRIPE ONLY. No figures of ours beside it: the widget already
             shows the line items and the total it is going to charge, and a
             second set next to it can only ever agree by luck. */
          <section className="cb-buy-main cb-buy-main--solo">
            <div className="cb-buy-main-head">
              <button
                type="button"
                className="cb-buy-back"
                onClick={back}
                data-testid="cb-buy-back"
              >
                &#8592; {BUY_COPY.back}
              </button>
              <button
                type="button"
                className="cb-modal-close"
                onClick={onClose}
                aria-label={BUY_COPY.close}
              >
                &#215;
              </button>
            </div>

            <h3 className="cb-buy-payment-title">{BUY_COPY.paymentTitle}</h3>
            <p className="cb-buy-payment-sub">{BUY_COPY.paymentSub}</p>

            <StripeCheckoutPanel
              clientSecret={clientSecret}
              loadingTitle={BUY_COPY.bridgeTitle}
              loadingSub={BUY_COPY.bridgeSub}
              loadingNote={BUY_COPY.bridgeNote}
              footer={BUY_COPY.stripeFooter}
              testId="cb-buy-stripe"
              onComplete={() => {
                // Payment taken, no redirect. The modal stays exactly where it
                // is and moves to its own "crediting" step; the page behind it
                // is never remounted. `onPaid` starts the poll that waits for
                // the webhook to credit the briefs, and the allowance is
                // re-read from the API when it lands.
                if (onPaid) onPaid(sessionId);
              }}
            />
          </section>
        )}

        {step === "error" && (
          <>
            <div className="cb-buy-state" role="alert" data-testid="cb-buy-error">
              <span className="cb-buy-state-mark cb-buy-state-mark--warn" aria-hidden="true">
                !
              </span>
              <h3 className="cb-buy-state-title">{BUY_COPY.errorTitle}</h3>
              <p className="cb-buy-state-body">{error?.message || BUY_COPY.errorBody}</p>
              {error?.supportRef && (
                <p className="cb-buy-state-ref">{BUY_COPY.errorRef(error.supportRef)}</p>
              )}
            </div>
            <div className="cb-buy-foot cb-buy-foot--pair">
              <button type="button" className="cb-buy-cta" onClick={start} data-testid="cb-buy-retry">
                {BUY_COPY.tryAgain}
              </button>
              <button type="button" className="cb-buy-secondary" onClick={onClose}>
                {BUY_COPY.notNow}
              </button>
            </div>
          </>
        )}

        {step === "crediting" && (
          <>
            <div className="cb-buy-state" role="status" data-testid="cb-buy-crediting">
              {/* Not the checkmark: the credit has not landed yet, and a tick
                  here reads as "done" a few seconds before the real done step
                  shows the receipt — two success moments for one purchase. */}
              <span className="cb-buy-state-mark cb-buy-state-mark--loading" aria-hidden="true" />
              <h3 className="cb-buy-state-title">{BUY_COPY.creditingTitle}</h3>
              <p className="cb-buy-state-body">{BUY_COPY.creditingBody}</p>
            </div>
            <div className="cb-buy-foot">
              {/* Offered, not forced: this normally resolves in seconds, but
                  nobody should be held in a modal waiting for a webhook. */}
              <button type="button" className="cb-buy-secondary" onClick={onClose}>
                {BUY_COPY.backToBriefs}
              </button>
            </div>
          </>
        )}

        {step === "pending" && (
          <>
            <div className="cb-buy-state" role="status" data-testid="cb-buy-pending">
              <span className="cb-buy-state-mark cb-buy-state-mark--ok" aria-hidden="true">
                &#10003;
              </span>
              <h3 className="cb-buy-state-title">{BUY_COPY.pendingTitle}</h3>
              <p className="cb-buy-state-body">{BUY_COPY.pendingBody}</p>
            </div>
            <div className="cb-buy-foot">
              <button type="button" className="cb-buy-cta" onClick={onClose}>
                {BUY_COPY.backToBriefs}
              </button>
            </div>
          </>
        )}

        {step === "done" && (
          <>
            <div className="cb-buy-state" data-testid="cb-buy-done">
              <span className="cb-buy-state-mark cb-buy-state-mark--ok" aria-hidden="true">
                &#10003;
              </span>
              <p className="cb-buy-state-eyebrow">{BUY_COPY.successEyebrow}</p>
              <h3 className="cb-buy-state-title">{BUY_COPY.successTitle(purchase.quantity)}</h3>
              <p className="cb-buy-state-body">{BUY_COPY.successBody}</p>
            </div>

            <div className="cb-buy-receipt">
              <div className="cb-buy-row">
                <span className="cb-buy-row-label">{BUY_COPY.chargedLabel}</span>
                <span className="cb-buy-row-value" data-testid="cb-buy-charged">
                  {formatAed(purchase.chargedTotalFils)}
                </span>
              </div>
              {/* KEPT, not conditional. The design's receipt block is three
                  rows and a receipt link, and the layout is reviewed against
                  it — so the rows hold their place and fill in when the
                  backend sends `reference` and `receipt_url`, which the state
                  endpoint does not yet return.

                  An EMPTY value, never a placeholder figure or an em dash:
                  this is a receipt, and inventing anything in it would be
                  inventing a fact about money. */}
              <div className="cb-buy-row">
                <span className="cb-buy-row-label">{BUY_COPY.referenceLabel}</span>
                <span className="cb-buy-row-value" data-testid="cb-buy-reference">
                  {purchase.reference || ""}
                </span>
              </div>
              {purchase.bought != null && (
                <div className="cb-buy-row">
                  <span className="cb-buy-row-label">{BUY_COPY.balanceLabel}</span>
                  <span className="cb-buy-row-value">
                    {BUY_COPY.balanceValue(purchase.bought)}
                  </span>
                </div>
              )}
            </div>

            <div className="cb-buy-foot cb-buy-foot--pair">
              <button type="button" className="cb-buy-cta" onClick={onOrder}>
                {BUY_COPY.orderABrief}
              </button>
              {/* Stripe's own invoice. The affordance is kept either way, so
                  the footer matches the design — but DISABLED without a URL
                  rather than an anchor to nowhere. A button that looks live
                  and does nothing is worse than one that plainly cannot be
                  pressed, and on a receipt it invites a support call. */}
              {purchase.receiptUrl ? (
                <a
                  className="cb-buy-secondary"
                  href={purchase.receiptUrl}
                  target="_blank"
                  rel="noreferrer"
                  data-testid="cb-buy-receipt"
                >
                  {BUY_COPY.viewReceipt}
                </a>
              ) : (
                <button
                  type="button"
                  className="cb-buy-secondary"
                  disabled
                  title={BUY_COPY.receiptPending}
                  data-testid="cb-buy-receipt"
                >
                  {BUY_COPY.viewReceipt}
                </button>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

// Exported for its own test: the money is the part worth pinning.
export { OrderSummary };
export default BuyBriefsModal;
